import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { Search, ArrowRight } from 'lucide-react';
import { useTranslation } from '@/contexts/LanguageContext';

export default function SearchAutocomplete({ 
  value, onChange, onSubmit, placeholder, className = '', inputClassName = '', 
  buttonClassName = '', buttonLabel, variant = 'default' 
}) {
  const navigate = useNavigate();
  const { t: tSector } = useTranslation('sectors');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchSuggestions = useCallback(async (q) => {
    if (q.length < 2) { setSuggestions([]); return; }
    try {
      const { data } = await api.get(`/listings/autocomplete?q=${encodeURIComponent(q)}`);
      setSuggestions(data.suggestions || []);
    } catch { setSuggestions([]); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (value.length < 2) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
      setOpen(true);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [value, fetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (text) => {
    onChange(text);
    setOpen(false);
    setActiveIdx(-1);
    navigate(`/annonces?q=${encodeURIComponent(text)}`);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(prev => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(prev => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIdx].text);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
    }
  };

  const isHero = variant === 'hero';
  const getImageSrc = (img) => {
    if (!img) return null;
    if (img.startsWith('http')) return img;
    return `${process.env.REACT_APP_BACKEND_URL}${img}`;
  };

  return (
    <div ref={wrapperRef} className={`relative flex-1 ${className}`}>
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isHero ? 'w-5 h-5' : 'w-4 h-4'} text-neutral-400`} />
        <Input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => { onChange(e.target.value); setActiveIdx(-1); }}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          className={`${isHero ? 'pl-10 h-12' : 'pl-9'} ${inputClassName}`}
          data-testid="search-autocomplete-input"
          autoComplete="off"
        />
      </div>

      {open && suggestions.length > 0 && (
        <div 
          className="absolute z-50 top-full left-0 right-0 mt-1 bg-white rounded-lg border border-neutral-200 shadow-lg overflow-hidden"
          data-testid="search-autocomplete-dropdown"
        >
          {suggestions.map((s, i) => (
            <button
              key={s.text + '_' + s.type}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                i === activeIdx ? 'bg-blue-50' : 'hover:bg-neutral-50'
              }`}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(s.text); }}
              onMouseEnter={() => setActiveIdx(i)}
              data-testid={`suggestion-${i}`}
            >
              {s.image ? (
                <img src={getImageSrc(s.image)} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded bg-neutral-100 flex items-center justify-center shrink-0">
                  <Search className="w-3.5 h-3.5 text-neutral-400" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-neutral-800 truncate">{s.text}</p>
                {s.sector && (
                  <p className="text-xs text-neutral-400">{tSector(s.sector)}</p>
                )}
              </div>
              {s.price != null && (
                <span className="text-sm font-semibold text-[#1F3F72] shrink-0">{s.price}€</span>
              )}
              <ArrowRight className="w-3.5 h-3.5 text-neutral-300 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
